import type { ReactElement } from "react";

import { SWReactCountryIcons } from "@similarweb/icons";
import { Button } from "@similarweb/ui-components";

import { useTrack } from "pro-features/components/WithTrack/src/useTrack";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import WrappedWithQuotaTooltip from "app/pages/sales-intelligence/common-components/WrappedWithQuotaTooltip/WrappedWithQuotaTooltip";
import useStaticListSizeQuota from "app/pages/sales-intelligence/hooks/useStaticListSizeQuota";
import {
    CountryIconWithText,
    RecommendationTile,
} from "pro-features/pages/workspace/common components/RecommendationsSidebar/RecommendationsSidebar";
import { ButtonSidesWrapper } from "pro-features/pages/workspace/common components/RecommendationsSidebar/StyledComponents";
import type { ICountryObject } from "app/services/CountryService";
import CountryService from "app/services/CountryService";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type { ListRecommendationType } from "../../../types";

import {
    StyledRecommendationListCountryTitle,
    StyledRecommendationListItemWrapper,
    StyledRecommendationListTitle,
} from "./styles";

export interface RecommendationsListBaseProps {
    onRecommendationsAdd(domains: { Domain: string }[]): void;
    onRecommendationDismiss(domain: string): void;
    onLinkClick(domain: string): void;
}

interface RecommendationsListProps extends RecommendationsListBaseProps {
    recommendationsList: readonly ListRecommendationType[];
    country: ICountryObject;
}

const RecommendationsList = ({
    recommendationsList,
    country,
    onRecommendationsAdd,
    onRecommendationDismiss,
    onLinkClick,
}: RecommendationsListProps): ReactElement => {
    const [track] = useTrack();
    const translate = useTranslation();
    const { limitOfWebsitesToTrack, numberOfTrackedSites, isListSizeQuotaReached } = useStaticListSizeQuota();

    const handleAddAllClick = () => {
        const availableLimit = limitOfWebsitesToTrack - numberOfTrackedSites;
        const recommendationsToAdd =
            recommendationsList.length > availableLimit
                ? recommendationsList.slice(0, availableLimit)
                : recommendationsList;
        onRecommendationsAdd(
            recommendationsToAdd.map((r) => ({
                Domain: r.Domain,
            })),
        );
    };

    return (
        <StyledRecommendationListItemWrapper>
            <FlexRow justifyContent="space-between">
                <StyledRecommendationListTitle>
                    <StyledRecommendationListCountryTitle>
                        <SWReactCountryIcons
                            countryCode={country.id}
                            size="xs"
                        />
                        <CountryIconWithText>{country.text}</CountryIconWithText>
                    </StyledRecommendationListCountryTitle>
                    <span>{translate("si.components.opportunities.recommendations_sidebar.similar_sites")}</span>
                </StyledRecommendationListTitle>

                <WrappedWithQuotaTooltip
                    placement="bottom"
                    isListSizeQuotaReached={isListSizeQuotaReached}
                >
                    <ButtonSidesWrapper>
                        <Button
                            type="flat"
                            onClick={handleAddAllClick}
                            dataAutomation="recommendations-sidebar-add-all"
                            isDisabled={isListSizeQuotaReached}
                        >
                            {translate("workspace.recommendation_sidebar.button")}
                        </Button>
                    </ButtonSidesWrapper>
                </WrappedWithQuotaTooltip>
            </FlexRow>

            {recommendationsList.map((recommendation) => (
                <RecommendationTile
                    key={recommendation.Domain}
                    track={track}
                    translate={translate}
                    onLinkRecommendation={onLinkClick}
                    onAddRecommendations={onRecommendationsAdd}
                    onDismissRecommendation={onRecommendationDismiss}
                    {...recommendation}
                    TopCountryName={CountryService.getCountryById(recommendation.TopCountry)?.text}
                />
            ))}
        </StyledRecommendationListItemWrapper>
    );
};

export default RecommendationsList;
